import { useMemo } from "react";
import { toIsoDate } from "../../utils/dates";
import "./MonthlyCalendar.css";

export type MonthDayCounts = {
  total_count: number;
  completed_count: number;
  active_count: number;
};

type Cell = { key: string; iso: string; dom: number; inMonth: boolean; muted: boolean };

const WEEKDAY_LABELS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function isoYmd(y: number, m1: number, d: number) {
  return `${y}-${pad2(m1)}-${pad2(d)}`;
}

function prevYm(y: number, m1: number) {
  if (m1 <= 1) return { y: y - 1, m: 12 };
  return { y, m: m1 - 1 };
}

function nextYm(y: number, m1: number) {
  if (m1 >= 12) return { y: y + 1, m: 1 };
  return { y, m: m1 + 1 };
}

function buildCells(year: number, month1: number): Cell[] {
  const first = new Date(year, month1 - 1, 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month1, 0).getDate();
  const { y: py, m: pm } = prevYm(year, month1);
  const prevLast = new Date(year, month1 - 1, 0).getDate();
  const out: Cell[] = [];

  for (let i = 0; i < startPad; i += 1) {
    const dom = prevLast - startPad + i + 1;
    const iso = isoYmd(py, pm, dom);
    out.push({ key: `p-${iso}`, iso, dom, inMonth: false, muted: true });
  }
  for (let dom = 1; dom <= daysInMonth; dom += 1) {
    const iso = isoYmd(year, month1, dom);
    out.push({ key: `c-${iso}`, iso, dom, inMonth: true, muted: false });
  }
  let { y: ty, m: tm } = nextYm(year, month1);
  let td = 1;
  while (out.length % 7 !== 0) {
    const iso = isoYmd(ty, tm, td);
    out.push({ key: `n-${iso}`, iso, dom: td, inMonth: false, muted: true });
    td += 1;
    const dimNext = new Date(ty, tm, 0).getDate();
    if (td > dimNext) {
      const n = nextYm(ty, tm);
      ty = n.y;
      tm = n.m;
      td = 1;
    }
  }
  return out;
}

function heatClass(total: number): string {
  if (total <= 0) return "";
  if (total <= 3) return " kp-month-cal__cell--heat1";
  if (total <= 8) return " kp-month-cal__cell--heat2";
  return " kp-month-cal__cell--heat3";
}

type Props = {
  year: number;
  month: number;
  countsByDate: Map<string, MonthDayCounts>;
  onSelectDay: (isoDate: string) => void;
};

export function MonthlyCalendar({ year, month, countsByDate, onSelectDay }: Props) {
  const todayIso = useMemo(() => toIsoDate(new Date()), []);
  const cells = useMemo(() => buildCells(year, month), [year, month]);

  return (
    <div className="kp-month-cal">
      <div className="kp-month-cal__weekdays" role="row">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="kp-month-cal__weekday" role="columnheader">
            {label}
          </div>
        ))}
      </div>
      <div className="kp-month-cal__grid" role="grid" aria-label="Ay takvimi">
        {cells.map((c) => {
          const counts = countsByDate.get(c.iso);
          const total = counts?.total_count ?? 0;
          const isToday = c.iso === todayIso;
          const heat = c.inMonth ? heatClass(total) : "";
          return (
            <button
              key={c.key}
              type="button"
              role="gridcell"
              disabled={!c.inMonth}
              className={`kp-month-cal__cell${c.muted ? " kp-month-cal__cell--muted" : ""}${c.inMonth ? heat : ""}${
                isToday && c.inMonth ? " kp-month-cal__cell--today" : ""
              }`}
              onClick={() => {
                if (c.inMonth) onSelectDay(c.iso);
              }}
              aria-label={
                c.inMonth
                  ? `${c.dom} ${total > 0 ? `, ${total} randevu` : ""}${isToday ? ", bugün" : ""}`
                  : undefined
              }
            >
              <span className="kp-month-cal__dom">{c.dom}</span>
              {c.inMonth && total > 0 ? (
                <span className="kp-month-cal__badge" title={`Tamamlanan: ${counts?.completed_count ?? 0}`}>
                  {total}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
